/**
 * Receiving (goods in): deliveries typed by hand or read from a scanned
 * invoice, checked by the chef, then received into stock. Beta feature
 * `stock` (R-4); backend B8 (backend/pleach/api/deliveries.py).
 *
 *   GET    /api/deliveries                       list, newest first, a page at a time
 *   POST   /api/deliveries                       new draft
 *   GET    /api/deliveries/suppliers             the supplier datalist
 *   GET    /api/deliveries/{id}                  one delivery with its lines
 *   PUT    /api/deliveries/{id}                  Save (version-checked, D-1)
 *   DELETE /api/deliveries/{id}?version=         delete a draft for good
 *   POST   /api/deliveries/{id}/document         upload the invoice (and read it)
 *   POST   /api/deliveries/{id}/read             read the stored invoice again
 *   POST   /api/deliveries/{id}/impact           dishes before and after the ticked prices
 *   POST   /api/deliveries/{id}/receive          stock in, prices, aliases
 *   POST   /api/deliveries/{id}/void             stock back off; prices stay
 *
 * Every figure comes back from the server. Money is ex GST (R-1); "not set"
 * is null, never 0. Always use `document.url` as the API sends it: it
 * carries the stored file's id, so a retaken photo is a new URL.
 *
 * Receive and Void move stock and (Receive) prices, so on success they
 * refresh everything that shows either: the count sheet, the order list,
 * the ingredients, and every costed view (recipes, menus, the dashboard,
 * prep lists). The live `costs.changed` event does the same for other tabs.
 */
import {
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { api, rawFetch, unwrap } from '@/api/client';
import type { components } from '@/api/schema';
import { registerLive } from '@/lib/live';
import { refreshAfterStockChange } from './stocktake';

export type Delivery = components['schemas']['DeliveryOut'];
export type DeliveryLine = components['schemas']['DeliveryLineOut'];
export type DeliveryLineIngredient = components['schemas']['DeliveryLineIngredientOut'];
export type DeliveryListItem = components['schemas']['DeliveryListItemOut'];
export type DeliveryList = components['schemas']['DeliveryListOut'];
export type DeliveryStatus = Delivery['status'];
export type DeliveryCreateIn = components['schemas']['DeliveryCreateIn'];
export type DeliveryUpdateIn = components['schemas']['DeliveryUpdateIn'];
export type DeliveryLineIn = components['schemas']['DeliveryLineIn'];
export type DeliveryPriceChangeIn = components['schemas']['DeliveryPriceChangeIn'];
export type DeliveryImpact = components['schemas']['DeliveryImpactOut'];
export type DeliveryReceiveOut = components['schemas']['DeliveryReceiveOut'];
export type DeliveryVoidOut = components['schemas']['DeliveryVoidOut'];
export type DeliverySupplier = components['schemas']['DeliverySupplierOut'];
export type MatchKind = DeliveryLine['match'];
export type PackBasis = DeliveryLine['pack_basis'];
export type LineFlag = DeliveryLine['flags'][number];
export type DeliveryFlag = Delivery['flags'][number];

const DOMAIN = 'deliveries' as const;

export const deliveriesKeys = {
  all: [DOMAIN] as const,
  lists: [DOMAIN, 'list'] as const,
  list: (status: DeliveryStatus | 'all') => [DOMAIN, 'list', status] as const,
  detail: (id: number) => [DOMAIN, 'detail', id] as const,
  suppliers: [DOMAIN, 'suppliers'] as const,
  impact: (id: number, changes: string) => [DOMAIN, 'impact', id, changes] as const,
};

/** How many deliveries the list asks for at once (newest first). */
export const DELIVERY_PAGE = 100;

// ── Reads ──────────────────────────────────────────────────────────────────

/** The pages loaded so far as one list: every item once (a delivery made
 *  between two pages moves the rest down one, so the next page can repeat
 *  the last one), with the first page's counts and total. */
export function joinDeliveryPages(data: InfiniteData<DeliveryList, number>): DeliveryList {
  const seen = new Set<number>();
  const items: DeliveryListItem[] = [];
  for (const page of data.pages) {
    for (const item of page.items) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      items.push(item);
    }
  }
  return { ...data.pages[0], items };
}

/** The next page's offset, or undefined once every delivery is loaded. */
export function nextDeliveryOffset(last: DeliveryList, pages: DeliveryList[]): number | undefined {
  const loaded = pages.reduce((n, p) => n + p.items.length, 0);
  return last.items.length > 0 && loaded < last.total ? loaded : undefined;
}

/** Deliveries newest first, DELIVERY_PAGE at a time: `data` is every page
 *  loaded so far as one list, and `fetchNextPage` loads the next (a busy
 *  kitchen passes 100 deliveries in about three weeks). A refresh (Save,
 *  a live event) reloads every page that is loaded. */
export function useDeliveries(status: DeliveryStatus | 'all' = 'all', enabled = true) {
  return useInfiniteQuery({
    queryKey: deliveriesKeys.list(status),
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET('/api/deliveries', {
          params: { query: { status: status === 'all' ? undefined : status, limit: DELIVERY_PAGE, offset: pageParam } },
        }),
      ),
    initialPageParam: 0,
    getNextPageParam: nextDeliveryOffset,
    select: joinDeliveryPages,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function deliveryDetailQuery(id: number) {
  return queryOptions({
    queryKey: deliveriesKeys.detail(id),
    queryFn: () => unwrap(api.GET('/api/deliveries/{delivery_id}', { params: { path: { delivery_id: id } } })),
  });
}

export function useDelivery(id: number | null, enabled = true) {
  return useQuery({ ...deliveryDetailQuery(id ?? -1), enabled: enabled && id !== null });
}

/** The kitchen's suppliers, for the editor's datalist. A failure only means
 *  no suggestions, so it never retries loudly. */
export function useDeliverySuppliers(enabled = true) {
  return useQuery({
    queryKey: deliveriesKeys.suppliers,
    queryFn: () => unwrap(api.GET('/api/deliveries/suppliers')),
    staleTime: 60_000,
    retry: false,
    enabled,
  });
}

/**
 * The impact preview for the ticked price changes on screen (unsaved ticks
 * included). A query keyed on the changes, so an answer that lands late for
 * an older set never replaces the current one; the previous answer stays on
 * screen while the next is on its way. `changesKey` is '' when there is
 * nothing ticked, and nothing is fetched.
 */
export function useDeliveryImpact(id: number | null, changes: DeliveryPriceChangeIn[], changesKey: string, enabled = true) {
  return useQuery({
    queryKey: deliveriesKeys.impact(id ?? -1, changesKey),
    queryFn: () =>
      unwrap(
        api.POST('/api/deliveries/{delivery_id}/impact', {
          params: { path: { delivery_id: id ?? -1 } },
          body: { changes },
        }),
      ),
    enabled: enabled && id !== null && changesKey !== '',
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 15_000,
  });
}

// ── Writes ─────────────────────────────────────────────────────────────────

function afterWrite(qc: QueryClient, d: Delivery) {
  qc.setQueryData(deliveriesKeys.detail(d.id), d);
  void qc.invalidateQueries({ queryKey: deliveriesKeys.lists });
}

/** Everything a receive or a void can move: stock (the count sheet, the
 *  order list, the ingredients' on hand, the dashboard's stock value) and,
 *  for a receive, prices (every costed view). */
function afterStockMove(qc: QueryClient) {
  refreshAfterStockChange(qc);
  void qc.invalidateQueries({ queryKey: deliveriesKeys.all });
  void qc.invalidateQueries({
    predicate: (q) => ['recipes', 'menus', 'dashboard', 'ingredients', 'prep-lists'].includes(String(q.queryKey[0])),
  });
}

export async function createDelivery(body: DeliveryCreateIn): Promise<Delivery> {
  return unwrap(api.POST('/api/deliveries', { body }));
}

export async function updateDelivery(id: number, body: DeliveryUpdateIn): Promise<Delivery> {
  return unwrap(api.PUT('/api/deliveries/{delivery_id}', { params: { path: { delivery_id: id } }, body }));
}

export function useCreateDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createDelivery,
    onSuccess: (d) => afterWrite(qc, d),
  });
}

/** Explicit Save (D-1): the header and the whole line list in one
 *  version-checked PUT. A 409 comes back as ApiError `conflict` with the
 *  saved delivery in `body.current`, for ConflictDialog. */
export function useUpdateDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: DeliveryUpdateIn }) => updateDelivery(id, body),
    onSuccess: (d) => afterWrite(qc, d),
  });
}

export function useDeleteDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: number; version: number }) =>
      unwrap(api.DELETE('/api/deliveries/{delivery_id}', { params: { path: { delivery_id: id }, query: { version } } })),
    onSuccess: (_d, { id }) => {
      qc.removeQueries({ queryKey: deliveriesKeys.detail(id) });
      void qc.invalidateQueries({ queryKey: deliveriesKeys.lists });
    },
  });
}

/** Store the invoice on a draft (replacing any earlier one) and, when
 *  invoice reading is set up, read it: a sync call of 10 to 30 s, up to 60.
 *  Answers with the draft as it now stands (`extraction_status` says how
 *  the read went). A read fills the lines only on a draft with none, unless
 *  `replace`: then its lines (and the details it finds) replace the draft's. */
export async function uploadDeliveryDocument(
  id: number,
  version: number,
  file: Blob,
  name: string,
  { replace = false }: { replace?: boolean } = {},
): Promise<Delivery> {
  const form = new FormData();
  form.append('file', file, name);
  form.append('version', String(version));
  if (replace) form.append('replace', 'true');
  const r = await rawFetch(`/api/deliveries/${id}/document`, { method: 'POST', body: form });
  return (await r.json()) as Delivery;
}

export function useReadAgain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version, replace }: { id: number; version: number; replace: boolean }) =>
      unwrap(
        api.POST('/api/deliveries/{delivery_id}/read', {
          params: { path: { delivery_id: id } },
          body: { version, replace },
        }),
      ),
    onSuccess: (d) => afterWrite(qc, d),
  });
}

export function useReceiveDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      version,
      allowDuplicate,
      ingredientVersions,
    }: {
      id: number;
      version: number;
      allowDuplicate?: boolean;
      /** Ingredient id to the version the chef checked: one that has moved
       *  on since is a 409 ingredient_changed. */
      ingredientVersions?: Record<string, number>;
    }) =>
      unwrap(
        api.POST('/api/deliveries/{delivery_id}/receive', {
          params: { path: { delivery_id: id } },
          body: { version, allow_duplicate: Boolean(allowDuplicate), ingredient_versions: ingredientVersions ?? null },
        }),
      ),
    retry: false,
    onSuccess: (out) => {
      qc.setQueryData(deliveriesKeys.detail(out.delivery.id), out.delivery);
      afterStockMove(qc);
    },
  });
}

export function useVoidDelivery() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, version }: { id: number; version: number }) =>
      unwrap(api.POST('/api/deliveries/{delivery_id}/void', { params: { path: { delivery_id: id } }, body: { version } })),
    retry: false,
    onSuccess: (out) => {
      qc.setQueryData(deliveriesKeys.detail(out.delivery.id), out.delivery);
      afterStockMove(qc);
    },
  });
}

// Other tabs and other people: any delivery event moves the list; an open
// editor re-reads its delivery (and keeps an unsaved draft, see the editor).
// An ingredient change can rename a matched line or move its price.
const refreshDeliveries = (_evt: unknown, qc: QueryClient) => void qc.invalidateQueries({ queryKey: deliveriesKeys.all });
registerLive('delivery.', refreshDeliveries);
registerLive('ingredient.', (_evt, qc) => {
  void qc.invalidateQueries({ queryKey: [DOMAIN, 'detail'] });
  void qc.invalidateQueries({ queryKey: deliveriesKeys.suppliers });
});
